import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  Sparkles, ChevronLeft, ChevronRight, Star, Ban, RotateCcw, Circle,
  CheckCircle2, Lock, CalendarCheck2, Users, Info,
} from "lucide-react";

const MONTHS_LONG = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function dayOfMonth(iso) { return Number(iso.slice(8, 10)); }
function monthOf(iso) { return Number(iso.slice(5, 7)) - 1; }
function isoOfDay(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
function formatFriendlyDate(iso) {
  const d = new Date(iso + "T12:00:00");
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

// One canonical state for each Saturday — drives icon + label + available actions.
// Priorities: closed > generated > scheduled > open.
function computeVisualState(sat, isSelected) {
  if (sat.state === "closed") {
    return { key: "closed", label: sat.is_holiday ? "Holiday" : "Closed",
      icon: Lock, color: "slate", tone: "bg-slate-100 text-slate-500 border-slate-300 line-through" };
  }
  if (sat.state === "generated") {
    const total = sat.total_slots || 0;
    const filled = sat.filled_slots || 0;
    const full = total > 0 && filled >= total;
    return {
      key: full ? "full" : "generated",
      label: full ? "Full" : "Generated",
      icon: full ? CheckCircle2 : CalendarCheck2,
      color: full ? "green-solid" : "green",
      tone: full
        ? "bg-brand-green text-white border-brand-green"
        : "bg-brand-green/15 text-brand-green-dark border-brand-green/40",
    };
  }
  if (isSelected) {
    return { key: "scheduled", label: "Scheduled", icon: Sparkles, color: "blue",
      tone: "bg-brand-blue text-white border-brand-blue" };
  }
  return { key: "open", label: "Open", icon: Circle, color: "white",
    tone: "bg-white text-slate-700 border-slate-300 hover:border-brand-blue hover:bg-brand-blue/5" };
}

function SaturdayCell({ sat, isSelected, onToggle, onCloseDate, onReopenDate }) {
  const state = computeVisualState(sat, isSelected);
  const Icon = state.icon;
  const d = dayOfMonth(sat.date);
  const total = sat.total_slots || 0;
  const filled = sat.filled_slots || 0;
  const hasHolidayNearby = (sat.nearby_holidays || []).length > 0;

  // Content of the button (compact — day number + tiny icon + coverage if any).
  const cellContent = (
    <button
      data-testid={`saturday-${sat.date}`}
      data-state={state.key}
      className={`group relative aspect-square rounded-md flex flex-col items-center justify-center gap-0.5 text-xs font-bold border-2 transition-all focus-ring ${state.tone}`}
    >
      <span className="text-[13px] leading-none">{d}</span>
      <Icon className="w-3 h-3 opacity-90" />
      {state.key !== "closed" && state.key !== "open" && total > 0 && (
        <span className="text-[9px] font-bold leading-none opacity-90">
          {filled}/{total}
        </span>
      )}
      {sat.is_holiday && (
        <span className="absolute -top-1 -right-1 text-amber-500" aria-label={`Holiday: ${sat.holiday_name}`}>
          <Star className="w-3 h-3 fill-amber-300" strokeWidth={2} />
        </span>
      )}
      {!sat.is_holiday && hasHolidayNearby && (
        <span className="absolute -top-1 -right-1 text-amber-400 opacity-70">
          <Star className="w-2.5 h-2.5" strokeWidth={2} />
        </span>
      )}
    </button>
  );

  // Menu contents change based on state.
  const closable = state.key === "generated" || state.key === "full";
  const reopenable = state.key === "closed";
  const selectable = state.key === "open" || state.key === "scheduled";

  return (
    <Popover>
      <PopoverTrigger asChild title={friendlyTooltip(sat, state)}>
        {cellContent}
      </PopoverTrigger>
      <PopoverContent align="center" className="w-72 p-3 space-y-2" data-testid={`popover-${sat.date}`}>
        <div className="pb-2 border-b border-slate-100">
          <p className="font-heading font-semibold text-slate-900 text-sm">
            {formatFriendlyDate(sat.date)}
          </p>
          <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
            <Icon className="w-3.5 h-3.5" /> {state.label}
            {sat.is_holiday && <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px] font-bold">{sat.holiday_name}</span>}
          </p>
          {state.key === "generated" || state.key === "full" ? (
            <p className="text-xs text-slate-600 mt-1.5 flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-slate-400" /> {filled}/{total} volunteers signed up
            </p>
          ) : null}
          {sat.nearby_holidays.length > 0 && !sat.is_holiday && (
            <p className="text-[11px] text-amber-700 mt-1.5">
              Near: {sat.nearby_holidays.map((h) => h.name).join(", ")}
            </p>
          )}
        </div>

        <div className="space-y-1">
          {(state.key === "generated" || state.key === "full") && (sat.shift_ids || []).length > 0 && (
            <a
              data-testid={`cal-view-roster-${sat.date}`}
              href={`/admin/shifts/${sat.shift_ids[0]}`}
              className="w-full flex items-center gap-2 px-2 py-2 text-sm text-brand-blue font-semibold hover:bg-brand-blue/10 rounded-md text-left focus-ring"
            >
              <Users className="w-4 h-4" /> View roster
            </a>
          )}
          {selectable && (
            <button
              data-testid={`cal-toggle-select-${sat.date}`}
              onClick={() => onToggle(sat.date)}
              className="w-full flex items-center gap-2 px-2 py-2 text-sm text-slate-700 hover:bg-brand-blue/10 hover:text-brand-blue rounded-md text-left focus-ring"
            >
              <Sparkles className="w-4 h-4" />
              {isSelected ? "Remove from selection" : "Include when generating"}
            </button>
          )}
          {closable && (
            <button
              data-testid={`cal-close-date-${sat.date}`}
              onClick={() => onCloseDate(sat.date, sat.is_holiday)}
              className="w-full flex items-center gap-2 px-2 py-2 text-sm text-slate-700 hover:bg-rose-50 hover:text-rose-700 rounded-md text-left focus-ring"
            >
              <Ban className="w-4 h-4" /> Close this Saturday
            </button>
          )}
          {reopenable && (
            <button
              data-testid={`cal-reopen-date-${sat.date}`}
              onClick={() => onReopenDate(sat.date)}
              className="w-full flex items-center gap-2 px-2 py-2 text-sm text-slate-700 hover:bg-brand-green/10 hover:text-brand-green-dark rounded-md text-left focus-ring"
            >
              <RotateCcw className="w-4 h-4" /> Reopen this Saturday
            </button>
          )}
          {state.key === "open" && !sat.in_blackout && (
            <button
              data-testid={`cal-mark-closed-${sat.date}`}
              onClick={() => onCloseDate(sat.date, sat.is_holiday)}
              className="w-full flex items-center gap-2 px-2 py-2 text-sm text-slate-700 hover:bg-rose-50 hover:text-rose-700 rounded-md text-left focus-ring"
            >
              <Lock className="w-4 h-4" /> Mark as {sat.is_holiday ? "holiday closed" : "closed"}
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function friendlyTooltip(sat, state) {
  const nice = formatFriendlyDate(sat.date);
  const lines = [nice, `Status: ${state.label}${sat.is_holiday ? ` (${sat.holiday_name})` : ""}`];
  if (state.key === "generated" || state.key === "full") {
    lines.push(`${sat.filled_slots || 0}/${sat.total_slots || 0} volunteers signed up`);
  } else if (state.key === "closed") {
    lines.push("No volunteer shifts will run.");
  } else if (state.key === "open") {
    lines.push("Click to include when generating shifts.");
  }
  lines.push("Click to change.");
  return lines.join("\n");
}

function MonthGrid({ year, monthIdx, saturdays, selected, onToggle, onCloseDate, onReopenDate, isCurrentMonth }) {
  const firstDay = new Date(year, monthIdx, 1);
  const lead = (firstDay.getDay() + 6) % 7;  // Mon=0..Sun=6
  const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const satByDay = {};
  saturdays.forEach((s) => {
    if (monthOf(s.date) === monthIdx) satByDay[dayOfMonth(s.date)] = s;
  });

  return (
    <div
      data-testid={`month-grid-${monthIdx}`}
      className={`rounded-xl border-2 bg-white p-3 sm:p-4 ${isCurrentMonth ? "border-brand-blue/50 shadow-sm ring-2 ring-brand-blue/10" : "border-slate-200"}`}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading text-lg font-semibold text-slate-900">
          {MONTHS_LONG[monthIdx]}
          {isCurrentMonth && <span className="ml-2 text-[10px] uppercase tracking-wider text-brand-blue font-bold">Now</span>}
        </h3>
      </div>
      <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wider text-slate-400 font-bold">
        {["M","T","W","T","F","S","S"].map((d, i) => <div key={i} className="text-center py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const sat = satByDay[d];
          const isoDate = isoOfDay(year, monthIdx, d);
          if (sat) {
            return (
              <SaturdayCell
                key={i}
                sat={sat}
                isSelected={selected.has(sat.date)}
                onToggle={onToggle}
                onCloseDate={onCloseDate}
                onReopenDate={onReopenDate}
              />
            );
          }
          return (
            <div key={i} data-testid={`day-${isoDate}`} className="aspect-square flex items-center justify-center text-xs text-slate-300">
              {d}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LegendChip({ icon: Icon, label, tone, count }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border-2 px-2.5 py-1 text-[11px] font-semibold ${tone}`}>
      <Icon className="w-3 h-3" /> {label}
      {typeof count === "number" && <span className="ml-0.5 opacity-80">({count})</span>}
    </span>
  );
}

export default function CalendarTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [showPastMonths, setShowPastMonths] = useState(false);
  const [confirmGenerate, setConfirmGenerate] = useState(false);
  const [confirmCloseHolidays, setConfirmCloseHolidays] = useState(false);
  const [confirmReopenAll, setConfirmReopenAll] = useState(false);
  const currentMonthRef = useRef(null);

  const load = async (y) => {
    try {
      const { data: r } = await api.get(`/admin/calendar/saturdays?year=${y}`);
      setData(r);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load calendar.");
    }
  };
  useEffect(() => { load(year); }, [year]);

  // Scroll the current month card into view once data loads.
  useEffect(() => {
    if (data && currentMonthRef.current) {
      requestAnimationFrame(() => {
        currentMonthRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [data, showPastMonths]);

  const toggle = (dt) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(dt)) next.delete(dt);
      else next.add(dt);
      return next;
    });
  };

  const selectAllRemainingOpen = () => {
    if (!data) return;
    const today = new Date().toISOString().slice(0, 10);
    const opens = data.saturdays.filter((s) => s.state === "open" && s.date >= today).map((s) => s.date);
    if (opens.length === 0) return toast.info("No open Saturdays to select.");
    setSelected(new Set(opens));
    toast.success(`Selected ${opens.length} open Saturday${opens.length === 1 ? "" : "s"}.`);
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/shifts/bulk-generate", { dates: Array.from(selected) });
      const skippedClosed = r.skipped_dates?.length || 0;
      const msg = `Generated ${r.created} shift${r.created === 1 ? "" : "s"}.`
        + (r.skipped > 0 ? ` Skipped ${r.skipped} already-existing.` : "")
        + (skippedClosed > 0 ? ` Skipped ${skippedClosed} closed date${skippedClosed === 1 ? "" : "s"}.` : "");
      toast.success(msg);
      setSelected(new Set());
      setConfirmGenerate(false);
      load(year);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const closeDate = async (iso, isHoliday) => {
    try {
      const { data: r } = await api.post(`/admin/dates/${iso}/cancel`);
      toast.success(`${isHoliday ? "Holiday" : "Saturday"} closed — ${r.cancelled_shifts} shift${r.cancelled_shifts === 1 ? "" : "s"} cancelled.`);
      load(year);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const reopenDate = async (iso) => {
    try {
      const { data: r } = await api.post(`/admin/dates/${iso}/restore`);
      toast.success(`Saturday reopened — ${r.restored_shifts} shift${r.restored_shifts === 1 ? "" : "s"} restored.`);
      load(year);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const closeAllFederalHolidays = async () => {
    try {
      const { data: r } = await api.post(`/admin/dates/close-federal-holidays?year=${year}`);
      toast.success(`Closed ${r.closed_count} holiday Saturday${r.closed_count === 1 ? "" : "s"}.`);
      setConfirmCloseHolidays(false);
      load(year);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setConfirmCloseHolidays(false);
    }
  };
  const reopenAllClosed = async () => {
    try {
      const { data: r } = await api.post(`/admin/dates/reopen-all?year=${year}`);
      toast.success(`Reopened ${r.reopened_count} Saturday${r.reopened_count === 1 ? "" : "s"}.`);
      setConfirmReopenAll(false);
      load(year);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setConfirmReopenAll(false);
    }
  };

  const goToToday = () => {
    const t = new Date();
    if (t.getFullYear() !== year) setYear(t.getFullYear());
    else {
      // already correct year — just scroll
      currentMonthRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const currentMonthIdx = now.getFullYear() === year ? now.getMonth() : -1;
  const monthsToShow = useMemo(() => {
    if (showPastMonths || currentMonthIdx < 0) return MONTHS_LONG.map((_, i) => i);
    return MONTHS_LONG.map((_, i) => i).filter((i) => i >= currentMonthIdx);
  }, [showPastMonths, currentMonthIdx]);
  const visibleMonthSet = useMemo(() => new Set(monthsToShow), [monthsToShow]);

  // Counts reflect ONLY the months currently visible on screen. Toggling
  // "Show past N months" expands the counts to include the hidden months.
  const summary = useMemo(() => {
    if (!data) return { total: 0, open: 0, generated: 0, closed: 0, full: 0, selectedInView: 0 };
    let open = 0, gen = 0, closed = 0, full = 0, sel = 0;
    for (const s of data.saturdays) {
      if (!visibleMonthSet.has(monthOf(s.date))) continue;
      if (selected.has(s.date)) sel += 1;
      if (s.state === "closed") closed += 1;
      else if (s.state === "generated") {
        gen += 1;
        if (s.total_slots > 0 && s.filled_slots >= s.total_slots) full += 1;
      } else open += 1;
    }
    return { total: 0, open, generated: gen, closed, full, selectedInView: sel };
  }, [data, visibleMonthSet, selected]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-semibold text-slate-900">Annual sign-up calendar</h2>
          <p className="text-sm text-slate-600 max-w-2xl">
            Click any Saturday to change its status — <b>Open</b>, <b>Closed</b>, or <b>Holiday</b> — or include it when generating volunteer shifts.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="cal-today-button" variant="outline" onClick={goToToday} className="border-slate-300">Today</Button>
          <Button data-testid="cal-year-prev" variant="ghost" size="icon" onClick={() => setYear(year - 1)}><ChevronLeft className="w-5 h-5" /></Button>
          <span className="font-heading text-xl font-bold text-slate-900 min-w-[3.5ch] text-center" data-testid="cal-year-label">{year}</span>
          <Button data-testid="cal-year-next" variant="ghost" size="icon" onClick={() => setYear(year + 1)}><ChevronRight className="w-5 h-5" /></Button>
        </div>
      </div>

      {/* Legend + summary */}
      <div className="flex flex-wrap items-center gap-2" data-testid="calendar-legend">
        <LegendChip icon={Circle} label="Open" tone="border-slate-300 bg-white text-slate-700" count={summary.open} />
        <LegendChip icon={Sparkles} label="Scheduled" tone="border-brand-blue bg-brand-blue text-white" count={summary.selectedInView} />
        <LegendChip icon={CalendarCheck2} label="Generated" tone="border-brand-green/40 bg-brand-green/15 text-brand-green-dark" count={summary.generated - summary.full} />
        <LegendChip icon={CheckCircle2} label="Full" tone="border-brand-green bg-brand-green text-white" count={summary.full} />
        <LegendChip icon={Lock} label="Closed" tone="border-slate-300 bg-slate-100 text-slate-600" count={summary.closed} />
        <LegendChip icon={Star} label="Holiday" tone="border-amber-300 bg-amber-50 text-amber-800" />
      </div>

      {/* Action bar */}
      <div className="rounded-xl border-2 border-slate-200 bg-white p-3 sm:p-4 flex flex-wrap items-center justify-between gap-2" data-testid="cal-action-bar">
        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="cal-select-all-open"
            variant="outline"
            onClick={selectAllRemainingOpen}
            className="border-slate-300 min-h-[40px]"
          >
            <Sparkles className="w-4 h-4 mr-1.5" /> Select all open
          </Button>
          <Button
            data-testid="cal-close-holidays"
            variant="outline"
            onClick={() => setConfirmCloseHolidays(true)}
            className="border-amber-300 text-amber-800 hover:bg-amber-50 min-h-[40px]"
          >
            <Star className="w-4 h-4 mr-1.5" /> Close federal holidays
          </Button>
          <Button
            data-testid="cal-reopen-all"
            variant="outline"
            onClick={() => setConfirmReopenAll(true)}
            disabled={summary.closed === 0}
            className="border-slate-300 min-h-[40px]"
          >
            <RotateCcw className="w-4 h-4 mr-1.5" /> Reopen all closed
          </Button>
        </div>
        <Button
          data-testid="cal-generate-button"
          onClick={() => setConfirmGenerate(true)}
          disabled={busy || selected.size === 0}
          className="bg-brand-blue hover:bg-brand-blue-dark min-h-[40px] px-5 font-semibold"
        >
          <Sparkles className="w-4 h-4 mr-1.5" /> Generate {selected.size > 0 ? `(${selected.size})` : ""}
        </Button>
      </div>

      {/* Show past months toggle */}
      {currentMonthIdx > 0 && (
        <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
          <Info className="w-3.5 h-3.5" />
          <span>
            Counts reflect the <b>{monthsToShow.length}</b> month{monthsToShow.length === 1 ? "" : "s"} shown ({MONTHS_SHORT[monthsToShow[0]]}–{MONTHS_SHORT[monthsToShow[monthsToShow.length - 1]]}).
          </span>
          <button
            data-testid="cal-toggle-past-months"
            onClick={() => setShowPastMonths((v) => !v)}
            className="font-semibold text-brand-blue hover:underline focus-ring"
          >
            {showPastMonths ? "Hide past months" : `Show past ${currentMonthIdx} month${currentMonthIdx === 1 ? "" : "s"}`}
          </button>
        </div>
      )}

      {/* Grids */}
      {!data && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-64 rounded-xl" />)}
        </div>
      )}
      {data && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {monthsToShow.map((m) => (
            <div key={m} ref={m === currentMonthIdx ? currentMonthRef : null}>
              <MonthGrid
                year={year}
                monthIdx={m}
                saturdays={data.saturdays}
                selected={selected}
                onToggle={toggle}
                onCloseDate={closeDate}
                onReopenDate={reopenDate}
                isCurrentMonth={m === currentMonthIdx}
              />
            </div>
          ))}
        </div>
      )}

      {/* Confirm: generate */}
      <AlertDialog open={confirmGenerate} onOpenChange={setConfirmGenerate}>
        <AlertDialogContent data-testid="cal-generate-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Generate {selected.size} volunteer shift{selected.size === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This creates volunteer signup opportunities on {selected.size} Saturday{selected.size === 1 ? "" : "s"} using your active route templates. Existing shifts and closed dates are skipped safely.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cal-generate-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="cal-generate-confirm-button" onClick={generate} className="bg-brand-blue hover:bg-brand-blue-dark">Generate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm: close holidays */}
      <AlertDialog open={confirmCloseHolidays} onOpenChange={setConfirmCloseHolidays}>
        <AlertDialogContent data-testid="cal-close-holidays-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Close all federal-holiday Saturdays in {year}?</AlertDialogTitle>
            <AlertDialogDescription>
              Every Saturday within ±2 days of a U.S. federal holiday (New Year&apos;s, Memorial Day, Independence Day, Labor Day, Thanksgiving, Christmas, etc.) will be marked closed. Any existing shifts on those dates will be cancelled. You can reopen individual ones anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="cal-close-holidays-confirm-button" onClick={closeAllFederalHolidays} className="bg-amber-600 hover:bg-amber-700">Close holiday Saturdays</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm: reopen all */}
      <AlertDialog open={confirmReopenAll} onOpenChange={setConfirmReopenAll}>
        <AlertDialogContent data-testid="cal-reopen-all-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Reopen all closed Saturdays in {year}?</AlertDialogTitle>
            <AlertDialogDescription>
              Every Saturday you&apos;ve marked as closed or holiday this year will be reopened. Cancelled shifts will be restored to visible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="cal-reopen-all-confirm-button" onClick={reopenAllClosed} className="bg-brand-green hover:bg-brand-green-dark">Reopen all</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
